<template>
  <div class="card">
    <div class="bg-blue-light p-2">
      <TitleButton :showBtn="false" title="Payment Requisition" />

      <DateQuerySetter
        :inEnoughSpace="false"
        btnTitle="Go"
        @onClickGo="onClickGo"
      >
        <div class="col-12 col-sm-6 col-md-4 col-lg-3">
          <div class="d-flex align-items-center justify-content-between filter-label">
            <label class="col-form-label">Party: </label>
            <span class="filter-search-icon cursor-pointer" @click="onOpenContactSearchModal">
              <i class="fas fa-search"></i>
            </span>
          </div>
          <AsyncSelect
            placeholder="Select Party"
            v-model="contactProfile"
            :api-service="fetchContactProfiles"
            :format-label="formatPatientLabel"
          />
        </div>
      </DateQuerySetter>
    </div>

    <div class="px-3 mt-2">
      <ul class="nav nav-tabs custom-nav-tab">
        <li v-for="tab in tabs" :key="tab.id" class="nav-item">
          <a
            :class="['nav-link', { active: activeTab === tab.id }]"
            @click="setActiveTab(tab.id)"
          >
            {{ tab.title }}
          </a>
        </li>
      </ul>
    </div>

    <div class="card mt-1 p-2">
      <div class="table-responsive">
        <table class="table table-bordered">
          <thead>
            <tr>
              <th>Voucher Create Date</th>
              <th>Voucher No</th>
              <th>Party Details</th>
              <th>Item / Account Head</th>
              <th class="text-end">Amount</th>
              <th>Attachment</th>
              <th>Cheque No</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="! rows.length">
              <td colspan="9" class="text-center">No data found.</td>
            </tr>
            <tr v-for="(item, i) in rows" :key="i">
              <td>{{ item.date }}</td>
              <td>{{ item.voucher_no }}</td>
              <td>{{ item.party_name }}</td>
              <td>{{ item.account_head_display }}</td>
              <td class="text-end">{{ commaFormat(item.amount) }}</td>
              <td>
                <button
                  v-if="item.attachments && item.attachments.length"
                  class="btn btn-outline-secondary btn-sm"
                  title="View attachment"
                  @click="viewAttachments(item.attachments)"
                >
                  <i class="fas fa-paperclip"></i>
                </button>
              </td>
              <td>
                <div class="d-flex align-items-center gap-2">
                  <span v-if="item.cheque_number">{{ item.cheque_number }}</span>
                  <button
                    v-if="activeTab === 'pending'"
                    class="btn btn-warning btn-sm text-nowrap"
                    @click="onClickChequeUpdate(item)"
                    :title="item.cheque_number ? 'Edit Cheque' : 'Add Cheque'"
                  >
                    {{ item.cheque_number ? 'Edit Cheque' : 'Add Cheque' }}
                  </button>
                </div>
              </td>
              <td>
                <span>{{ item.status }}</span>
              </td>
              <td>
                <button
                  v-if="activeTab === 'pending'"
                  class="btn btn-primary btn-sm"
                  @click="onClickRequest(item)"
                >Request</button>
                <button
                  v-else
                  class="btn btn-info btn-sm"
                  @click="onClickDetails(item)"
                  title="Details"
                >
                  <i class="fa fa-eye"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <Pagination
        :offset="offset"
        @onPageChange="onPageChange"
        ref="paginationRef"
      />

      <ContactDropdownFilter
        v-if="store.state.isModalOpenThree"
        @onSearchContact="onSearchContact"
        :isFilterable="true"
        :companyRoles="companyRoles"
      />
    </div>

    <SelectChequeForApprovalModal
      v-if="showChequeModal && activeLedger"
      :ledger-master-id="activeLedger.ledger_master_id"
      :preselected-cheque-id="activeLedger.cheque_register_id"
      :preselected-account-head-id="activeLedger.account_head_id"
      @close="onCloseChequeModal"
      @approved="onApprovalStarted"
    />

    <LedgerDetailsModal
      v-if="showDetailsModal && activeDetailsLedgerId"
      :ledger-master-id="activeDetailsLedgerId"
      @close="onCloseDetailsModal"
    />

    <Loader v-if="loading" />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, inject, watch } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import TitleButton from '@/components/atom/TitleButton'
import Loader from '@/components/atom/LoaderComponent'
import DateQuerySetter from '@/components/atom/DateQuerySetter'
import Pagination from '@/components/atom/Pagination'
import AsyncSelect from '@/components/molecule/input-field/AsyncSelect.vue'
import ContactDropdownFilter from '@/components/atom/Contact/ContactDropdownFilter.vue'
import SelectChequeForApprovalModal from './SelectChequeForApprovalModal.vue'
import LedgerDetailsModal from './LedgerDetailsModal.vue'
import handlePaymentRequisition from '@/services/modules/paymentRequisition'
import handleContact from '@/services/modules/contact'
import handleRole from '@/services/modules/role'
import handleCompany from '@/services/modules/company'
import handleBarcode from '@/services/modules/barcode'
import figureFormatter from '@/services/utils/figureFormatter'
import paymentRequestVoucherPdf from '@/services/utils/paymentRequestVoucherPdf'
import { useAsyncDropdownHelper } from '@/services/utils/asyncDropdownHelper'

const showError = inject('showError')
const showSuccess = inject('showSuccess')
const store = useStore()
const route = useRoute()
const router = useRouter()

const { fetchPaymentRequisitionList, startLedgerApproval, fetchPaymentRequisitionVoucherPdf } = handlePaymentRequisition()
const { fetchContactProfiles } = handleContact()
const { fetchCompanyInfo } = handleCompany()
const { fetchBarcode } = handleBarcode()
const { formatPatientLabel } = useAsyncDropdownHelper()
const { fetchCompanyDefaultRoles } = handleRole()
const { commaFormat } = figureFormatter()
const { exportToPDF } = paymentRequestVoucherPdf()

const tabs = [
  { id: 'pending', title: 'Pending' },
  { id: 'on_approval', title: 'On Approval' },
  { id: 'approved', title: 'Approved' },
]

const tabStatusMap = {
  pending: 'pending_approval',
  on_approval: 'pending_review,pending_approve,on_approval,pa_approved,rejected,returned',
  approved: 'approved',
}

const activeTab = ref('pending')
const loading = ref(false)
const rows = ref([])
const contactProfile = ref(null)
const companyRoles = ref([])
const offset = ref(20)
const paginationRef = ref(null)
const showChequeModal = ref(false)
const activeLedger = ref(null)
const showDetailsModal = ref(false)
const activeDetailsLedgerId = ref(null)

const companyId = computed(() => route.params.companyId)
const start = computed(() => route.query.start)
const end = computed(() => route.query.end)
const page = computed(() => route.query.page)

const getQuery = () => {
  let query = '?company_id=' + companyId.value
  if (start.value) query += '&start_date=' + start.value
  if (end.value) query += '&end_date=' + end.value
  if (page.value) query += '&page=' + page.value
  if (contactProfile.value) query += '&contact_profile_id=' + contactProfile.value.id
  query += '&status=' + tabStatusMap[activeTab.value]
  query += '&offset=' + offset.value
  return query
}

const setPagination = (data) => {
  if (! data) return
  store.commit('setPaginationData', {
    records: data.total,
    perPage: data.per_page,
    currentPage: data.current_page,
  })
}

const fetchData = async () => {
  loading.value = true
  rows.value = []
  try {
    const res = await fetchPaymentRequisitionList(getQuery())
    if (! res.status) {
      showError(res.message)
      store.commit('resetPagination')
      return
    }
    rows.value = res.data.data
    setPagination(res.data)
  } catch (err) {
    showError('Something is wrong. Check your connectivity!!')
  } finally {
    loading.value = false
  }
}

const setActiveTab = async (id) => {
  if (activeTab.value === id) return
  activeTab.value = id
  store.commit('setPaginationData', {})
  const routeQuery = Object.assign({}, route.query)
  routeQuery.page = 1
  await router.push({ path: route.path, query: routeQuery })
  await fetchData()
}

const onClickGo = async () => {
  store.commit('setPaginationData', {})
  const routeQuery = Object.assign({}, route.query)
  routeQuery.page = 1
  await router.push({ path: route.path, query: routeQuery })
  await fetchData()
}

const onPageChange = async (p) => {
  const routeQuery = Object.assign({}, route.query)
  routeQuery.page = p
  await router.push({ path: route.path, query: routeQuery })
  await fetchData()
}

const onClickChequeUpdate = (item) => {
  activeLedger.value = {
    ledger_master_id: item.ledger_master_id,
    cheque_register_id: item.cheque_register_id || null,
    account_head_id: item.cheque_account_head_id || null,
  }
  showChequeModal.value = true
}

const onClickRequest = async (item) => {
  if (! window.confirm('Are you sure you want to request?')) return
  loading.value = true
  try {
    const res = await startLedgerApproval(item.ledger_master_id, {
      company_id: companyId.value,
      page_id: Number(route.params.pageId) || null,
    })
    if (! res.status) {
      showError(res.message)
      return
    }
    showSuccess(res.message)
    await generateVoucherPdf(item.ledger_master_id)
    fetchData()
  } catch (err) {
    showError('Something is wrong. Check your connectivity!!')
  } finally {
    loading.value = false
  }
}

const generateVoucherPdf = async (ledgerMasterId) => {
  const companyQuery = `?company_id=${companyId.value}`
  const [pdfRes, company] = await Promise.all([
    fetchPaymentRequisitionVoucherPdf(ledgerMasterId, companyQuery),
    fetchCompanyInfo(companyId.value),
  ])
  if (! pdfRes.status) {
    showError(pdfRes.message)
    return
  }
  const voucher = pdfRes.data
  const qrcodeData = JSON.stringify({
    ref_no: voucher.voucher_no,
    ref_date: voucher.date,
    created_date_time: new Date().toISOString(),
  })
  const barcodeRes = await fetchBarcode(`?code=${voucher.voucher_no}&qrcode=${qrcodeData}`)
  exportToPDF(company.data, voucher, barcodeRes.qrcode)
}

const onApprovalStarted = () => {
  fetchData()
}

const onCloseChequeModal = () => {
  showChequeModal.value = false
  activeLedger.value = null
}

const onClickDetails = (item) => {
  activeDetailsLedgerId.value = item.ledger_master_id
  showDetailsModal.value = true
}

const onCloseDetailsModal = () => {
  showDetailsModal.value = false
  activeDetailsLedgerId.value = null
}

const viewAttachments = (attachments) => {
  if (! attachments || ! attachments.length) return
  window.open(attachments[0].path, '_blank')
}

const onOpenContactSearchModal = () => {
  store.state.isModalOpenThree = true
}

const onSearchContact = (contact) => {
  contactProfile.value = contact
}

const getCompanyRoles = async () => {
  const res = await fetchCompanyDefaultRoles(`?company_id=${companyId.value}`)
  if (! res.status) return
  companyRoles.value = res.data
}

onMounted(() => {
  store.commit('resetPagination')
  getCompanyRoles()
  fetchData()
})
</script>

<style scoped>
.filter-label {
  margin-bottom: 4px;
}
</style>
